import React, { useRef, useState } from 'react';
import { Animated, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { CommonActions, useNavigation, useNavigationState } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 64;
const ANIMATION_DURATION = 280;

function getRankLabel(level: number): string {
  if (level >= 20) return 'ELITE AGENT';
  if (level >= 10) return 'AGENT CERTIFIE';
  if (level >= 5) return 'TECHNICIEN';
  return 'RECRUE';
}

interface NavItemConfig {
  name: string;
  label: string;
  iconLib: 'ionicons' | 'fontawesome';
  icon: string;
  iconFocused?: string;
  badge?: number;
}

const NAV_ITEMS: NavItemConfig[] = [
  { name: 'Missions',        label: 'Missions',         iconLib: 'fontawesome', icon: 'bullseye' },
  { name: 'Chat',            label: 'Chat',             iconLib: 'ionicons',    icon: 'chatbox-outline',       iconFocused: 'chatbox',       badge: 3 },
  { name: 'Dashboard',       label: 'Dashboard',        iconLib: 'ionicons',    icon: 'bar-chart-outline',     iconFocused: 'bar-chart' },
  { name: 'Leaderboard',     label: 'Leaderboard',      iconLib: 'ionicons',    icon: 'trophy-outline',        iconFocused: 'trophy' },
  { name: 'Invoices',        label: 'Invoices',         iconLib: 'ionicons',    icon: 'document-text-outline', iconFocused: 'document-text' },
  { name: 'PaymentMethods',  label: 'Payment Methods',  iconLib: 'ionicons',    icon: 'card-outline',          iconFocused: 'card' },
  { name: 'Account',         label: 'Compte',           iconLib: 'ionicons',    icon: 'person-outline',        iconFocused: 'person' },
];

interface SideMenuProps {
  missionDetailScreens?: string[];
}

const PLAYER_LEVEL = 7;
const PLAYER_XP = 65;
const PLAYER_XP_MAX = 100;

function PlayerCard({ expanded, labelOpacity }: { expanded: boolean; labelOpacity: Animated.Value }) {
  const { playerName, playerAvatar } = usePlayer();
  const rank = getRankLabel(PLAYER_LEVEL);
  const xpPercent = Math.round((PLAYER_XP / PLAYER_XP_MAX) * 100);

  return (
    <View style={pcStyles.container}>
      <View style={pcStyles.avatarWrap}>
        {playerAvatar ? (
          <Image source={playerAvatar} style={pcStyles.avatar} resizeMode="cover" />
        ) : (
          <View style={pcStyles.avatarFallback}>
            <Text style={pcStyles.avatarInitial}>{(playerName ?? 'J')[0].toUpperCase()}</Text>
          </View>
        )}
        <View style={pcStyles.levelBadge}>
          <Text style={pcStyles.levelText}>{PLAYER_LEVEL}</Text>
        </View>
      </View>

      <Animated.View style={[pcStyles.info, { opacity: labelOpacity }]}>
        <Text style={pcStyles.name} numberOfLines={1}>{playerName ?? 'Joueur'}</Text>
        <Text style={pcStyles.rank}>{rank}</Text>
        <View style={pcStyles.xpRow}>
          <View style={pcStyles.xpTrack}>
            <View style={[pcStyles.xpFill, { width: `${xpPercent}%` as any }]} />
          </View>
          <Text style={pcStyles.xpLabel}>{PLAYER_XP}/{PLAYER_XP_MAX}</Text>
        </View>
      </Animated.View>
    </View>
  );
}

const pcStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 8,
    marginBottom: 8,
    marginTop: 4,
    padding: 10,
    backgroundColor: 'rgba(0,240,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(0,240,255,0.2)',
    borderRadius: 10,
    gap: 8,
    overflow: 'hidden',
  },
  avatarWrap: { position: 'relative', flexShrink: 0 },
  avatar: { width: 40, height: 40, borderRadius: 20 },
  avatarFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,240,255,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(0,240,255,0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: { color: '#00f0ff', fontSize: 16, fontWeight: '800' },
  levelBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: '#0a0e1a',
    borderWidth: 1,
    borderColor: '#00f0ff',
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  levelText: { color: '#00f0ff', fontSize: 9, fontWeight: '800', fontFamily: 'monospace' },
  info: { flex: 1, overflow: 'hidden' },
  name: { color: '#e8eaf6', fontSize: 12, fontWeight: '700' },
  rank: { color: '#00f0ff', fontSize: 9, fontWeight: '700', fontFamily: 'monospace', marginTop: 1, marginBottom: 4 },
  xpRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  xpTrack: {
    flex: 1,
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  xpFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    backgroundColor: '#00f0ff',
    borderRadius: 2,
    shadowColor: '#00f0ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  xpLabel: { color: '#8892a4', fontSize: 9, fontFamily: 'monospace', flexShrink: 0 },
});

export default function SideMenu({ missionDetailScreens = [] }: SideMenuProps) {
  const navigation = useNavigation<any>();
  const { isDarkTheme, toggleTheme } = useTheme();
  const { logout } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const [expanded, setExpanded] = useState(true);
  const widthAnim = useRef(new Animated.Value(EXPANDED_WIDTH)).current;
  const labelOpacity = useRef(new Animated.Value(1)).current;

  // Determine active route from the InnerStack (nested inside the 'Main' screen)
  const currentRouteName = useNavigationState(state => {
    const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
    const innerState = mainRoute?.state;
    if (innerState) {
      return innerState.routes?.[innerState.index ?? 0]?.name ?? 'Missions';
    }
    return 'Missions';
  });

  // Resolve which sidebar tab should be highlighted
  const getActiveTab = (): string => {
    if (missionDetailScreens.includes(currentRouteName)) return 'Missions';
    return currentRouteName;
  };
  const activeTab = getActiveTab();

  const toggle = () => {
    const toWidth = expanded ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    const toOpacity = expanded ? 0 : 1;
    Animated.timing(widthAnim, { toValue: toWidth, duration: ANIMATION_DURATION, useNativeDriver: false }).start();
    Animated.timing(labelOpacity, { toValue: toOpacity, duration: expanded ? 80 : ANIMATION_DURATION, useNativeDriver: true }).start();
    setExpanded(!expanded);
  };

  const renderIcon = (item: NavItemConfig, focused: boolean, color: string) => {
    if (item.iconLib === 'fontawesome') return <FontAwesome name={item.icon} size={20} color={color} />;
    const iconName = focused && item.iconFocused ? item.iconFocused : item.icon;
    return <Ionicons name={iconName} size={20} color={color} />;
  };

  const mainItems = NAV_ITEMS.filter(item => item.name !== 'Account');
  const accountItem = NAV_ITEMS.find(item => item.name === 'Account')!;

  return (
    <Animated.View style={[styles.container, { width: widthAnim, backgroundColor: colors.tabBackground, borderRightColor: colors.border, shadowColor: colors.cardShadow }]}>
      {/* Toggle button */}
      <TouchableOpacity onPress={toggle} style={[styles.toggleBtn, { borderBottomColor: colors.border }]} activeOpacity={0.7}>
        <View style={styles.iconContainer}>
          <Ionicons name={expanded ? 'chevron-back' : 'menu'} size={20} color={colors.iconSecondary} />
        </View>
      </TouchableOpacity>

      {/* Main nav items */}
      <View style={styles.navItems}>
        {mainItems.map(item => {
          const focused = activeTab === item.name;
          const color = focused ? colors.tabActive : colors.tabInactive;
          return (
            <TouchableOpacity
              key={item.name}
              onPress={() => navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{ name: 'Main', state: { routes: [{ name: item.name }], index: 0 } }],
                })
              )}
              style={[styles.navItem, { backgroundColor: focused ? colors.tabActiveBackground : 'transparent' }]}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>
                {renderIcon(item, focused, color)}
                {!!item.badge && (
                  <View style={styles.iconBadge}>
                    <Text style={styles.iconBadgeText}>{item.badge}</Text>
                  </View>
                )}
              </View>
              <Animated.Text style={[styles.navLabel, { color, opacity: labelOpacity }]} numberOfLines={1}>
                {item.label}
              </Animated.Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Bottom section */}
      <View style={styles.bottomSection}>
        {/* Player Card */}
        <PlayerCard expanded={expanded} labelOpacity={labelOpacity} />

        {/* Account item */}
        {(() => {
          const focused = activeTab === accountItem.name;
          const color = focused ? colors.tabActive : colors.tabInactive;
          return (
            <TouchableOpacity
              onPress={() => navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [{ name: 'Main', state: { routes: [{ name: accountItem.name }], index: 0 } }],
                })
              )}
              style={[styles.navItem, styles.accountItem, { backgroundColor: focused ? colors.tabActiveBackground : 'transparent', borderTopColor: colors.border }]}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>{renderIcon(accountItem, focused, color)}</View>
              <Animated.Text style={[styles.navLabel, { color, opacity: labelOpacity }]} numberOfLines={1}>{accountItem.label}</Animated.Text>
            </TouchableOpacity>
          );
        })()}

        {/* Dark/Light theme toggle */}
        <TouchableOpacity onPress={toggleTheme} style={[styles.themeToggleBtn, { borderTopColor: colors.border }]} activeOpacity={0.7}>
          <View style={styles.iconContainer}>
            <Ionicons
              name={isDarkTheme ? 'sunny-outline' : 'moon-outline'}
              size={20}
              color={colors.iconSecondary}
            />
          </View>
          <Animated.Text style={[styles.navLabel, { color: colors.iconSecondary, opacity: labelOpacity }]} numberOfLines={1}>
            {isDarkTheme ? 'Mode clair' : 'Mode sombre'}
          </Animated.Text>
        </TouchableOpacity>

        {/* Logout */}
        <TouchableOpacity onPress={logout} style={[styles.logoutBtn, { borderTopColor: colors.border }]} activeOpacity={0.7}>
          <View style={styles.iconContainer}>
            <Ionicons name="log-out-outline" size={20} color={colors.error} />
          </View>
          <Animated.Text style={[styles.logoutLabel, { color: colors.error, opacity: labelOpacity }]} numberOfLines={1}>
            Deconnexion
          </Animated.Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: { height: '100%', borderRightWidth: 1, elevation: 4, shadowOffset: { width: 2, height: 0 }, shadowOpacity: 0.12, shadowRadius: 8, overflow: 'hidden', flexShrink: 0, flexDirection: 'column' },
  toggleBtn: { height: 50, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, flexShrink: 0 },
  iconContainer: { width: 32, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  navItems: { flex: 1, paddingTop: 4, overflow: 'hidden' },
  navItem: { flexDirection: 'row', alignItems: 'center', height: 44, borderRadius: 10, marginHorizontal: 6, marginVertical: 1, paddingHorizontal: 6 },
  accountItem: { borderTopWidth: 1, borderRadius: 10, marginHorizontal: 6, paddingHorizontal: 6 },
  navLabel: { flex: 1, marginLeft: 10, fontSize: 13, fontWeight: '600' },
  bottomSection: { flexShrink: 0 },
  themeToggleBtn: { flexDirection: 'row', alignItems: 'center', height: 44, paddingHorizontal: 6, marginHorizontal: 6, borderTopWidth: 1 },
  logoutBtn: { flexDirection: 'row', alignItems: 'center', height: 44, paddingHorizontal: 6, marginHorizontal: 6, borderTopWidth: 1, marginBottom: 8 },
  logoutLabel: { flex: 1, marginLeft: 10, fontSize: 13, fontWeight: '600' },
  iconBadge: { position: 'absolute', top: -4, right: -4, backgroundColor: '#EF4444', borderRadius: 10, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  iconBadgeText: { color: '#FFFFFF', fontSize: 11, fontWeight: 'bold' },
});
